import { cn } from '../../lib/utils';

/**
 * Table components matching design system specifications
 * Standardized table components that match form_styles.md patterns exactly
 * All table text uses text-xs (12px) to comply with typography constraint
 */

/**
 * Table - Main table container with overflow handling
 */
export const Table = ({ children, className, ...props }) => {
  return (
    <div className={cn('relative pr-8 pl-8', className)} {...props}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {children}
        </table>
      </div>
    </div>
  );
};

Table.displayName = 'Table';

/**
 * TableHeader - Table header section
 */
export const TableHeader = ({ children, className, ...props }) => {
  return (
    <thead className={className} {...props}>
      {children}
    </thead>
  );
};

TableHeader.displayName = 'TableHeader';

/**
 * TableBody - Table body section
 */
export const TableBody = ({ children, className, ...props }) => {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
};

TableBody.displayName = 'TableBody';

/**
 * TableRow - Table row with hover effects
 */
export const TableRow = ({ children, className, isHeader = false, ...props }) => {
  if (isHeader) {
    return (
      <tr 
        className={cn(
          'bg-gray-50 dark:bg-gray-900/50',
          'border-b border-gray-200 dark:border-gray-700',
          className
        )} 
        {...props}
      >
        {children}
      </tr>
    );
  }
  
  return (
    <tr 
      className={cn(
        'border-b border-gray-200 dark:border-gray-700',
        'hover:bg-gray-50 dark:hover:bg-gray-700/30',
        className
      )} 
      {...props}
    >
      {children}
    </tr>
  );
};

TableRow.displayName = 'TableRow';

/**
 * TableHead - Table header cell
 * Text size: text-xs (12px) to comply with typography constraint
 */
export const TableHead = ({ children, className, align = 'left', ...props }) => {
  return (
    <th 
      className={cn(
        'text-left py-3 px-4',
        'text-xs font-semibold text-gray-700 dark:text-gray-300',
        'uppercase tracking-wider',
        {
          'text-left': align === 'left',
          'text-right': align === 'right',
          'text-center': align === 'center',
        },
        className
      )} 
      {...props}
    >
      {children}
    </th>
  );
};

TableHead.displayName = 'TableHead';

/**
 * TableCell - Table data cell
 * Text size: text-xs (12px) to comply with typography constraint
 */
export const TableCell = ({ children, className, align = 'left', ...props }) => {
  return (
    <td 
      className={cn(
        'py-2 px-4',
        'text-xs text-gray-900 dark:text-gray-100',
        {
          'text-left': align === 'left',
          'text-right': align === 'right',
          'text-center': align === 'center',
        },
        className
      )} 
      {...props}
    >
      {children}
    </td>
  );
};

TableCell.displayName = 'TableCell';
